import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  ChatCircle,
  Lightning,
  CheckCircle,
  ArrowRight,
  CircleNotch,
  XCircle,
  CalendarBlank,
  Users,
  MapPin,
} from "@phosphor-icons/react";

/**
 * EventInquiriesTile — Pro-owner inbox preview, lives on /dashboard
 * right below Calendar Sync.
 *
 * Lists the 5 most recent inquiries delivered to any truck the owner
 * owns, with status pills and click-through to the per-inquiry reply
 * page at /dashboard/inquiries/:recipientId. The Navbar badge tells
 * owners *that* they have new inquiries; this tile is the answer to
 * "how do I open them?"
 */
export default function EventInquiriesTile() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/owner/event-inquiries", { params: { limit: 5 } })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
        setNewCount(r.data?.new_count || 0);
      })
      .catch(() => {
        // Silently ignore — failed fetch just hides the tile content
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Hide the entire tile when there are no inquiries at all + we're done
  // loading. Avoids a permanently-empty box on dashboards before the
  // marketplace gets traction. Owners with at least one inquiry (even
  // already-replied) keep seeing the list.
  if (!loading && items.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="event-inquiries-tile"
    >
      <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center flex-none">
          <ChatCircle weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-base tracking-tight flex items-center gap-2">
            Event inquiries
            {newCount > 0 && (
              <span
                className="text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                data-testid="event-inquiries-new-badge"
              >
                {newCount} new
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Catering & event leads from the marketplace
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground">
          <CircleNotch weight="bold" className="h-5 w-5 mx-auto animate-spin text-primary" />
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map(({ recipient, inquiry }) => (
            <li key={recipient.id}>
              <Link
                to={`/dashboard/inquiries/${recipient.id}`}
                className="block px-5 py-4 hover:bg-muted/40 transition"
                data-testid={`inquiry-row-${recipient.id}`}
              >
                <div className="flex items-start gap-3">
                  <StatusPill status={recipient.status} kind={recipient.response_kind} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{inquiry.customer_name}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground capitalize">{inquiry.event_type}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <CalendarBlank weight="duotone" className="h-3.5 w-3.5" />
                        {inquiry.event_date_start}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Users weight="duotone" className="h-3.5 w-3.5" />
                        {inquiry.party_size}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin weight="duotone" className="h-3.5 w-3.5" />
                        {inquiry.event_zip}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
                      {inquiry.message}
                    </p>
                  </div>
                  <ArrowRight weight="bold" className="h-4 w-4 text-muted-foreground flex-none mt-1" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusPill({ status, kind }) {
  if (status === "replied") {
    const isDecline = kind === "decline";
    return (
      <div
        className={
          "h-6 w-6 rounded-full grid place-items-center flex-none mt-0.5 " +
          (isDecline ? "bg-muted text-muted-foreground" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400")
        }
        title={isDecline ? "Declined" : "Replied"}
      >
        {isDecline
          ? <XCircle weight="fill" className="h-4 w-4" />
          : <CheckCircle weight="fill" className="h-4 w-4" />}
      </div>
    );
  }
  if (status === "viewed") {
    return (
      <div className="h-6 w-6 rounded-full grid place-items-center bg-amber-500/15 text-amber-600 dark:text-amber-400 flex-none mt-0.5" title="Open · awaiting your reply">
        <Lightning weight="fill" className="h-4 w-4" />
      </div>
    );
  }
  // "delivered" = new
  return (
    <div className="h-6 w-6 rounded-full grid place-items-center bg-primary/15 text-primary flex-none mt-0.5 relative" title="New">
      <Lightning weight="fill" className="h-4 w-4" />
      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-card" />
    </div>
  );
}
